type NetworkResponse = Response & {
  data?: Record<string, unknown>;
};

const NO_CONTENT = 204;

const dfc = (window as any).dfc;

export class StatsigMicrolib {
  private readonly clientKey: string;

  constructor(clientKey: string) {
    this.clientKey = clientKey;
  }

  private _getUserId() {
    const name = '_ga';
    return document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)')?.pop() || '';
  }

  private _getStableId() {
    const key = 'dfc-statsig-stable-id';
    const val = localStorage.getItem(key);
    if (val) {
      return val;
    }
    const random = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
    localStorage.setItem(key, random);
    return random;
  }

  private getConfig() {
    let tier: 'production' | 'staging' | 'dev';

    if (dfc.env === null || dfc.env === 'main') {
      tier = 'production';
    } else if (dfc.env === 'staging') {
      tier = 'staging';
    } else {
      tier = 'dev';
    }

    return {
      user: {
        userID: this._getUserId(),
        customIDs: {
          stableID: this._getStableId(),
        },
        statsigEnvironment: { tier },
      },
    };
  }

  private sendBeacon(url: string, body: object) {
    const botRegex = /\b(bot|crawler|spider|slurp)\b/i;
    if (botRegex.test(navigator.userAgent)) return null;

    const params: RequestInit = {
      method: 'POST',
      body: JSON.stringify(body),
      headers: {
        'Content-type': 'application/json; charset=UTF-8',
        'STATSIG-API-KEY': this.clientKey,
      },
    };

    return fetch(url, params).then(async (res) => {
      if (res.ok) {
        const networkResponse = res as NetworkResponse;
        if (res.status === NO_CONTENT) {
          networkResponse.data = { has_updates: false, is_no_content: true };
        } else {
          const text = await res.text();
          networkResponse.data = JSON.parse(text);
        }
        return Promise.resolve(networkResponse);
      }
      const errorText = await res.text();
      return Promise.reject(new Error(`${res.status}: ${errorText}`));
    });
  }

  async onBucket(_expName: string, _expIdx: number | string) {
    /*global dfc*/
    const expName = _expName.toLowerCase();
    const expIdx = parseInt(_expIdx.toString(), 10);
    const dedupeMs = 1000 * 60 * 60 * 24 * 7; // 7 days
    const ns = `dfc-statsig-${expName}`;
    const value = dfc.getWithExpiry(ns);
    if (parseInt(value, 10) === expIdx) return;
    dfc.setWithExpiry(ns, expIdx, dedupeMs);

    return this.sendBeacon(`https://events.statsigapi.net/v1/log_custom_exposure`, {
      exposures: [
        {
          ...this.getConfig(),
          experimentName: expName,
          group: `v${expIdx}`,
        },
      ],
    });
  }

  async onEvent(eventName: string, eventValue?: string | number) {
    const event = {
      ...this.getConfig(),
      time: Date.now(),
      eventName: eventName,
    };
    if (eventValue !== undefined) {
      (event as any).value = eventValue;
    }
    return this.sendBeacon('https://events.statsigapi.net/v1/log_event', { events: [event] });
  }
}
